import { type FC, Fragment, useState, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { MOBILE } from 'dibs-buyer-layout/exports/deviceTypeEnums';
import serverVars from 'server-vars';

import { SellerApplicationDescription } from './SellerApplicationDescription';
import { SellerApplicationByTheNumbers } from './SellerApplicationByTheNumbers';
import { SellerApplicationBenefits } from './SellerApplicationBenefits';
import SellerApplicationForm from './form/SellerApplicationForm';
import { trackSellerApplicationEvent } from './helpers/sellerApplicationTracking';
import { type SellerApplicationWrapper_viewer$key } from './__generated__/SellerApplicationWrapper_viewer.graphql';

const viewerFragment = graphql`
    fragment SellerApplicationWrapper_viewer on Viewer {
        ...SellerApplicationBenefits_viewer
        ...SellerApplicationForm_viewer @arguments(leadId: $leadId)
    }
`;

type DeviceType = 'desktop' | 'tablet' | 'mobile';
type Props = {
    viewer: SellerApplicationWrapper_viewer$key;
    deviceType: DeviceType;
};

function deviceTypeIsMobile(deviceType?: DeviceType): boolean {
    return (serverVars.get('deviceType') || deviceType) === MOBILE;
}

const SellerApplicationWrapper: FC<Props> = ({ viewer: viewerRef, deviceType }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const [showForm, setShowForm] = useState(false);
    const isMobile = deviceTypeIsMobile(deviceType);
    const formRef = useRef<HTMLDivElement>(null);
    const handleApplyNow = (): void => {
        trackSellerApplicationEvent({
            type: 'main',
            action: 'entry',
            interactionName: 'application_entry',
            stepInteractionType: 'entry to application',
        });
        setShowForm(true);
        if (typeof formRef?.current?.scrollIntoView === 'function')
            formRef.current.scrollIntoView({
                block: 'center',
                inline: 'nearest',
                behavior: 'smooth',
            });
    };

    return (
        <Fragment>
            <SellerApplicationDescription
                isMobile={isMobile}
                showForm={showForm}
                handleApplyNow={handleApplyNow}
            />
            <SellerApplicationByTheNumbers />
            <SellerApplicationBenefits isMobile={isMobile} viewer={viewer} />

            <SellerApplicationForm
                viewer={viewer}
                handleApplyNow={handleApplyNow}
                showForm={showForm}
                formRef={formRef}
            />
        </Fragment>
    );
};

export default SellerApplicationWrapper;
